import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Field } from "../pages/AuthShell";
import { api, errMsg } from "../lib/api";

const Section = ({ title, desc, children, testId }) => (
  <section className="rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-8" data-testid={testId}>
    <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">{title}</h2>
    {desc && <p className="mt-1 text-sm text-[#4B5A54]">{desc}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

const SaveButton = ({ busy, testId, label = "Save changes" }) => (
  <button type="submit" disabled={busy} className="btn-primary" data-testid={testId}>
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : label}
  </button>
);

const useSubmit = (fn, success) => {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fn();
      toast.success(success);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return [busy, submit];
};

export const ProfileForm = ({ user, onSaved }) => {
  const [form, setForm] = useState({ name: user.name, email: user.email, current_password: "" });
  const emailChanged = form.email.trim().toLowerCase() !== user.email;
  const [busy, submit] = useSubmit(async () => {
    await api.put("/account/profile", { ...form, current_password: form.current_password || null });
    setForm((f) => ({ ...f, current_password: "" }));
    await onSaved();
  }, "Profile updated");
  return (
    <Section title="Your profile" desc="Your name and the email you log in with." testId="account-profile-section">
      <form onSubmit={submit} className="space-y-4 max-w-md">
        <Field label="Your name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name-input" />
        <Field label="Login email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="account-email-input" />
        {emailChanged && (
          <Field label="Current password (required to change email)" type="password" required value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="account-email-password-input" />
        )}
        <SaveButton busy={busy} testId="account-profile-save" />
      </form>
    </Section>
  );
};

export const PasswordForm = () => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, submit] = useSubmit(async () => {
    if (form.new_password !== form.confirm) throw new Error("New passwords don't match");
    await api.put("/account/password", { current_password: form.current_password, new_password: form.new_password });
    setForm({ current_password: "", new_password: "", confirm: "" });
  }, "Password changed");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Section title="Password" testId="account-password-section">
      <form onSubmit={submit} className="space-y-4 max-w-md">
        <Field label="Current password" type="password" required value={form.current_password} onChange={set("current_password")} data-testid="account-current-password-input" autoComplete="current-password" />
        <Field label="New password" type="password" required minLength={6} value={form.new_password} onChange={set("new_password")} data-testid="account-new-password-input" autoComplete="new-password" />
        <Field label="Confirm new password" type="password" required minLength={6} value={form.confirm} onChange={set("confirm")} data-testid="account-confirm-password-input" autoComplete="new-password" />
        <SaveButton busy={busy} testId="account-password-save" label="Change password" />
      </form>
    </Section>
  );
};

const LocationForm = ({ loc, onSaved }) => {
  const [form, setForm] = useState({ name: loc.name, location: loc.location || "", category: loc.category || "", alert_email: loc.alert_email || loc.owner_email });
  const [busy, submit] = useSubmit(async () => {
    await api.put(`/business/locations/${loc.id}`, { ...form, location: form.location || null, category: form.category || null });
    await onSaved();
  }, `${form.name} updated`);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 p-5 space-y-4" data-testid={`account-location-form-${loc.slug}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Business name" required minLength={2} value={form.name} onChange={set("name")} data-testid={`account-location-name-${loc.slug}`} />
        <Field label="Location / neighborhood" optional value={form.location} onChange={set("location")} data-testid={`account-location-location-${loc.slug}`} />
        <Field label="Business type" optional value={form.category} onChange={set("category")} data-testid={`account-location-category-${loc.slug}`} />
        <Field label="Alert email" type="email" required value={form.alert_email} onChange={set("alert_email")} data-testid={`account-location-alert-email-${loc.slug}`} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-[#778580]">Survey link stays the same: /s/{loc.slug}</p>
        <SaveButton busy={busy} testId={`account-location-save-${loc.slug}`} />
      </div>
    </form>
  );
};

export const LocationsForm = ({ locations, onSaved }) => (
  <Section title="Business & locations" desc="Update how each location appears on its survey, dashboard and emails." testId="account-locations-section">
    <div className="space-y-4">
      {locations.map((l) => <LocationForm key={l.id} loc={l} onSaved={onSaved} />)}
    </div>
  </Section>
);
